import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

function Profile() {
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [slopesFriendLink, setSlopesFriendLink] = useState('');
  const [epicFriendLink, setEpicFriendLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const isDevMode = localStorage.getItem('devMode') === 'true';

  useEffect(() => {
    if (!currentUser && !isDevMode) {
      navigate('/');
      return;
    }

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate, isDevMode]);

  const loadProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const userId = isDevMode ? 'dev-user' : (currentUser?.uid || 'unknown');
      
      // Get original name from localStorage (set during login)
      const storedName = localStorage.getItem('userName') || '';
      setOriginalName(storedName);
      
      // Try to load profile from Firestore
      if (db && userId !== 'unknown') {
        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setDisplayName(userData.displayName || storedName);
            setSlopesFriendLink(userData.slopesFriendLink || '');
            setEpicFriendLink(userData.epicFriendLink || '');
            
            // If originalName wasn't set, use the one from profile
            if (!storedName && userData.originalName) {
              setOriginalName(userData.originalName);
            }
          } else {
            // No profile exists yet, use localStorage name as default
            setDisplayName(storedName);
            
            // Create initial profile with original name
            if (storedName) {
              await setDoc(userDocRef, {
                originalName: storedName,
                displayName: storedName,
                slopesFriendLink: '',
                epicFriendLink: '',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
            }
          }
        } catch (firestoreError) {
          console.warn('Error loading from Firestore, using localStorage:', firestoreError);
          // Fallback to localStorage
          setDisplayName(storedName);
        }
      } else {
        // Dev mode or no Firestore, use localStorage
        setDisplayName(storedName);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Extract URL from slopes/epic app copy format
  const extractUrlFromPaste = (text) => {
    if (!text) return '';
    
    // Try to find URLs in the text
    // Pattern for slopes: "https://my.getslopes.com/app/addFriend/..."
    // Pattern for epic: "https://..." (epic pass links)
    const urlPattern = /https?:\/\/[^\s]+/g;
    const matches = text.match(urlPattern);
    
    if (matches && matches.length > 0) {
      // Return the first URL found
      return matches[0].trim();
    }
    
    // If no URL found, return the trimmed text
    return text.trim();
  };

  const handleSlopesPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const extractedUrl = extractUrlFromPaste(pastedText);
    setSlopesFriendLink(extractedUrl);
  };

  const handleEpicPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const extractedUrl = extractUrlFromPaste(pastedText);
    setEpicFriendLink(extractedUrl);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const userId = isDevMode ? 'dev-user' : (currentUser?.uid || 'unknown');
      
      if (!displayName.trim()) {
        setError('Display name cannot be empty');
        setSaving(false);
        return;
      }

      // Update Firestore
      if (db && userId !== 'unknown') {
        const userDocRef = doc(db, 'users', userId);
        const existingDoc = await getDoc(userDocRef);
        
        const profileData = {
          displayName: displayName.trim(),
          slopesFriendLink: slopesFriendLink.trim(),
          epicFriendLink: epicFriendLink.trim(),
          updatedAt: new Date().toISOString()
        };

        if (existingDoc.exists()) {
          // Update existing profile, preserve originalName
          await updateDoc(userDocRef, profileData);
        } else {
          // Create new profile with originalName
          await setDoc(userDocRef, {
            originalName: originalName || displayName.trim(),
            ...profileData,
            createdAt: new Date().toISOString()
          });
        }
      }

      // Update localStorage for backward compatibility
      localStorage.setItem('userName', displayName.trim());

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">👤 Profile</h1>
          <p className="card-subtitle">Manage your profile information</p>
        </div>

        {error && (
          <div style={{ 
            background: '#fed7d7', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #feb2b2'
          }}>
            <p style={{ margin: 0, color: '#c53030', fontSize: '0.9rem' }}>
              {error}
            </p>
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#c6f6d5', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #9ae6b4'
          }}>
            <p style={{ margin: 0, color: '#2f855a', fontSize: '0.9rem', fontWeight: '600' }}>
              ✅ Profile saved successfully!
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Profile Photo Section - Coming Soon */}
          <div style={{ 
            background: 'var(--bg-secondary)', 
            padding: '20px', 
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              margin: '0 auto 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: 'white',
              fontWeight: 'bold',
              border: '4px solid var(--bg-primary)',
              boxShadow: '0 4px 8px var(--shadow)'
            }}>
              {displayName ? displayName.charAt(0).toUpperCase() : '?'}
            </div>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>
              Profile Photo
            </h3>
            <div style={{
              display: 'inline-block',
              background: '#fff3cd',
              color: '#856404',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '600',
              border: '1px solid #ffc107'
            }}>
              📸 Coming Soon
            </div>
            <p style={{ 
              margin: '10px 0 0 0', 
              color: 'var(--text-tertiary)', 
              fontSize: '0.9rem' 
            }}>
              Photo editing will be available in a future update
            </p>
          </div>

          {/* Display Name */}
          <div className="form-group">
            <label className="form-label" htmlFor="displayName">
              Display Name *
            </label>
            <input
              id="displayName"
              type="text"
              className="form-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              maxLength={50}
            />
            {originalName && originalName !== displayName && (
              <p style={{ 
                margin: '8px 0 0 0', 
                color: 'var(--text-tertiary)', 
                fontSize: '0.85rem',
                fontStyle: 'italic'
              }}>
                Original name: {originalName}
              </p>
            )}
          </div>

          {/* Slopes App Friend Link */}
          <div className="form-group">
            <label className="form-label" htmlFor="slopesFriendLink">
              Slopes App Friend Link
            </label>
            <input
              id="slopesFriendLink"
              type="url"
              className="form-input"
              value={slopesFriendLink}
              onChange={(e) => setSlopesFriendLink(e.target.value)}
              onPaste={handleSlopesPaste}
              placeholder="https://slopes.app/... or paste from Slopes app"
            />
            <p style={{ 
              margin: '8px 0 0 0', 
              color: 'var(--text-tertiary)', 
              fontSize: '0.85rem' 
            }}>
              Share your Slopes app friend link so others can connect with you. You can paste directly from the Slopes app!
            </p>
          </div>

          {/* Epic App Friend Link */}
          <div className="form-group">
            <label className="form-label" htmlFor="epicFriendLink">
              Epic App Friend Link
            </label>
            <input
              id="epicFriendLink"
              type="url"
              className="form-input"
              value={epicFriendLink}
              onChange={(e) => setEpicFriendLink(e.target.value)}
              onPaste={handleEpicPaste}
              placeholder="https://epicpass.com/... or paste from Epic app"
            />
            <p style={{ 
              margin: '8px 0 0 0', 
              color: 'var(--text-tertiary)', 
              fontSize: '0.85rem' 
            }}>
              Share your Epic app friend link so others can connect with you. You can paste directly from the Epic app!
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
            <button
              onClick={handleSave}
              className="btn btn-primary"
              disabled={saving || !displayName.trim()}
              style={{
                opacity: (saving || !displayName.trim()) ? 0.6 : 1,
                cursor: (saving || !displayName.trim()) ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? '💾 Saving...' : '💾 Save Changes'}
            </button>

            <button
              onClick={() => navigate('/home')}
              className="btn btn-secondary"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
